import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const OutletContext = createContext(null);

export const useOutlet = () => {
  const context = useContext(OutletContext);
  if (!context) {
    throw new Error('useOutlet must be used within OutletProvider');
  }
  return context;
};

export const OutletProvider = ({ children }) => {
  const { api, user } = useAuth();
  const [outlets, setOutlets] = useState([]);
  const [currentOutlet, setCurrentOutlet] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOutlets = useCallback(async () => {
    try {
      const response = await api.get('/outlets');
      setOutlets(response.data);
      
      // Set current outlet based on user's outlet_id
      if (user?.outlet_id) {
        const userOutlet = response.data.find(o => o.id === user.outlet_id);
        if (userOutlet) {
          setCurrentOutlet(userOutlet);
        } else if (response.data.length > 0) {
          setCurrentOutlet(response.data[0]);
        }
      } else if (response.data.length > 0) {
        setCurrentOutlet(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch outlets:', error);
    } finally {
      setLoading(false);
    }
  }, [api, user?.outlet_id]);

  useEffect(() => {
    if (user) {
      fetchOutlets();
    }
  }, [user, fetchOutlets]);

  const createOutlet = async (outletData) => {
    const response = await api.post('/outlets', outletData);
    setOutlets(prev => [...prev, response.data]);
    if (!currentOutlet) {
      setCurrentOutlet(response.data);
    }
    return response.data;
  };

  const switchOutlet = (outlet) => {
    setCurrentOutlet(outlet);
  };

  const value = {
    outlets,
    currentOutlet,
    loading,
    createOutlet,
    switchOutlet,
    fetchOutlets,
  };

  return <OutletContext.Provider value={value}>{children}</OutletContext.Provider>;
};
